<template>
  <div class="payment-icon" :style="iconStyle">
    <!-- Ícone do Iconify (se disponível) -->
    <Icon
      v-if="iconifyIcon"
      :icon="iconifyIcon"
      :width="size"
      :height="size"
      :style="{ color: color }"
    />
    <!-- SVG local (fallback) -->
    <img
      v-else-if="localIcon"
      :src="localIcon"
      :width="size"
      :height="size"
      :alt="name"
      :style="localIconStyle"
    />
    <!-- Fallback para ícone não encontrado -->
    <div
      v-else
      class="payment-icon-fallback"
      :style="fallbackStyle"
    >
      {{ name.toUpperCase() }}
    </div>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue2';

export default {
  name: 'PaymentIcon',
  components: {
    Icon
  },
  props: {
    name: {
      type: String,
      required: true
    },
    size: {
      type: [String, Number],
      default: 24
    },
    color: {
      type: String,
      default: 'rgba(0, 0, 0, 0.54)' // Cor padrão dos ícones do Vuetify
    }
  },
  computed: {
    iconifyIcon() {
      // Mapeamento dos ícones disponíveis no Iconify
      const iconifyMap = {
        'pix': 'simple-icons:pix',
        'mastercard': 'simple-icons:mastercard',
        'visa': 'simple-icons:visa',
        'amex': 'simple-icons:americanexpress',
        'american-express': 'simple-icons:americanexpress',
        'elo': 'logos:elo',
        'dinersclub': 'simple-icons:dinersclub',
        'discover': 'simple-icons:discover',
        'jcb': 'simple-icons:jcb',
        'maestro': 'logos:maestro',
        'paypal': 'simple-icons:paypal',
        'googlepay': 'simple-icons:googlepay',
        'applepay': 'simple-icons:applepay',
        'samsung-pay': 'simple-icons:samsungpay'
      };
      
      return iconifyMap[this.name.toLowerCase()] || null;
    },
    localIcon() {
      // Se não tem no Iconify, tenta carregar do assets local
      if (!this.iconifyIcon) {
        try {
          return require(`@/assets/icons/payment/${this.name.toLowerCase()}.svg`);
        } catch (e) {
          // Se também não tem local, retorna null para mostrar fallback
          return null;
        }
      }
      return null;
    },
    iconStyle() {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle'
      };
    },
    localIconStyle() {
      const style = {};
      if (this.color && this.isColorizable(this.name)) {
        style.filter = this.getSvgColorFilter(this.color);
      }
      return style;
    },
    fallbackStyle() {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
        backgroundColor: this.color || '#6c757d',
        color: 'white',
        fontSize: `${Math.max(8, parseInt(this.size) * 0.3)}px`,
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        textAlign: 'center'
      };
    }
  },
  methods: {
    isColorizable(iconName) {
      // Alguns ícones podem ser coloridos, outros devem manter a cor original
      const colorizableIcons = ['pix', 'generic', 'unknown'];
      return colorizableIcons.includes(iconName.toLowerCase());
    },
    getSvgColorFilter(color) {
      // Converte cor hex/rgb para filtro CSS (aproximado)
      // Para cores simples como #00BFA5 (PIX verde)
      if (color === '#00BFA5' || color === '#32BCAD') {
        return 'hue-rotate(160deg) saturate(1.2) brightness(0.9)';
      } else if (color.startsWith('#')) {
        // Filtro genérico para outras cores hex
        return `brightness(0) saturate(100%) ${this.hexToFilter(color)}`;
      }
      return 'none';
    },
    hexToFilter(hex) {
      // Converte hex para filtro CSS básico (simplificado)
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (result) {
        const r = parseInt(result[1], 16) / 255;
        const g = parseInt(result[2], 16) / 255;
        const b = parseInt(result[3], 16) / 255;
        
        // Cálculo simplificado de matiz
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0;
        
        if (max !== min) {
          const d = max - min;
          switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
          }
          h /= 6;
        }
        
        return `hue-rotate(${h * 360}deg)`;
      }
      return '';
    }
  }
};
</script>

<style scoped>
.payment-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.payment-icon-fallback {
  font-family: 'Arial', sans-serif;
  user-select: none;
}

/* Estilos específicos para alguns ícones */
.payment-icon img {
  object-fit: contain;
}
</style>